import { useState, useEffect } from "react";
import { Video } from "@/entities/Video";
import { Play, Search, X, Video as VideoIcon, RefreshCw } from "lucide-react";

const CATEGORIAS = ["Todos", "Música", "Conciertos", "Videoclips", "Entrevistas", "Variedad"];

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [categoria, setCategoria] = useState("Todos");
  const [busqueda, setBusqueda] = useState("");
  const [videoActivo, setVideoActivo] = useState(null);

  useEffect(() => {
    cargarVideos();
  }, []);

  async function cargarVideos() {
    setCargando(true);
    const data = await Video.filter({ activo: true }, "orden", 100);
    setVideos(data);
    setCargando(false);
  }

  const videosFiltrados = videos.filter((v) => {
    const matchCat = categoria === "Todos" || v.categoria === categoria;
    const matchBus =
      v.titulo?.toLowerCase().includes(busqueda.toLowerCase()) ||
      v.artista?.toLowerCase().includes(busqueda.toLowerCase());
    return matchCat && matchBus;
  });

  function getEmbedUrl(url) {
    if (!url) return "";
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0`;
    if (url.includes("youtube.com/embed")) return url;
    return url;
  }

  function getThumbnail(video) {
    if (video.miniatura) return video.miniatura;
    const ytMatch = video.url_video?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (ytMatch) return `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`;
    const embedMatch = video.url_video?.match(/youtube\.com\/embed\/([^?]+)/);
    if (embedMatch) return `https://img.youtube.com/vi/${embedMatch[1]}/hqdefault.jpg`;
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-primary text-primary-foreground px-6 py-4 flex items-center gap-3">
        <VideoIcon size={24} />
        <div>
          <h1 className="text-xl font-black">Videos</h1>
          <p className="text-xs text-white/70">Videoclips, conciertos y más</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="px-6 py-4 bg-secondary border-b border-border">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar por título o artista..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="w-full bg-background text-foreground text-sm pl-8 pr-3 py-2 rounded border border-border focus:outline-none focus:border-primary"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {CATEGORIAS.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoria(cat)}
                className={`text-xs px-3 py-2 rounded transition-all font-semibold ${
                  categoria === cat
                    ? "bg-primary text-primary-foreground"
                    : "bg-background text-foreground border border-border hover:bg-muted"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid de videos */}
      <div className="p-6">
        {cargando ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <RefreshCw size={24} className="animate-spin mr-2" />
            Cargando videos...
          </div>
        ) : videosFiltrados.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <VideoIcon size={48} className="mx-auto mb-3 opacity-30" />
            <p>No hay videos disponibles</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {videosFiltrados.map((video) => {
              const thumb = getThumbnail(video);
              return (
                <div
                  key={video.id}
                  className="bg-card rounded-lg overflow-hidden shadow hover:shadow-lg transition-all cursor-pointer group border border-border"
                  onClick={() => setVideoActivo(video)}
                >
                  <div className="relative aspect-video bg-gray-900">
                    {thumb ? (
                      <img src={thumb} alt={video.titulo} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <VideoIcon size={32} className="text-gray-600" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="bg-primary rounded-full p-3">
                        <Play size={20} className="text-white fill-white" />
                      </div>
                    </div>
                    {video.duracion && (
                      <span className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded">
                        {video.duracion}
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="font-bold text-sm text-foreground truncate">{video.titulo}</p>
                    {video.artista && <p className="text-xs text-muted-foreground truncate">{video.artista}</p>}
                    <span className="inline-block mt-1 text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded">
                      {video.categoria}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal reproductor */}
      {videoActivo && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-xl w-full max-w-3xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div>
                <p className="text-white font-bold">{videoActivo.titulo}</p>
                {videoActivo.artista && <p className="text-gray-400 text-xs">{videoActivo.artista}</p>}
              </div>
              <button
                onClick={() => setVideoActivo(null)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={22} />
              </button>
            </div>
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe
                src={getEmbedUrl(videoActivo.url_video)}
                className="absolute inset-0 w-full h-full"
                allowFullScreen
                allow="autoplay; encrypted-media"
                title={videoActivo.titulo}
              />
            </div>
            {videoActivo.descripcion && (
              <div className="px-4 py-3 text-gray-400 text-sm">{videoActivo.descripcion}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}